import { ShadowConfig } from '@/scenes/engine/common/interfaces'
import { hexToRgba } from '@/scenes/engine/utils/utils'

/**
 * Batch Studio renders shadows as a simple CSS `drop-shadow` filter rather than
 * the editor's PIXI `DropShadowHDFilter`. The shadow geometry mirrors the editor
 * math (see canvasImageRenderer.ts): `distance` and `blur` are fractions of the
 * drawn cutout's longest edge (maxEdge), `angle` is in radians, `opacity` is the
 * 0..1 alpha folded into the color.
 *
 * The same conversion drives both the live card preview and the baked export, so
 * the shadow stays identical relative to the image regardless of pixel size.
 */

/**
 * Slider logic min/max/default, copied from `DropShadowHDFilter` so Batch Studio
 * needs no PIXI dependency. Keep in sync with
 * src/scenes/engine/utils/PixijsFilters/DropShadowHDFilter.ts.
 */
export const SHADOW_RANGE = {
  opacity: { min: 0, max: 1.0, default: 0.4 },
  distance: { min: 0, max: 0.4, default: 0.06 },
  angle: { min: 0, max: 360, default: 45 }, // UI degrees
  blur: { min: 0, max: 0.3, default: 0.02 },
} as const

export const SHADOW_DEFAULT_COLOR = '#000000'

/**
 * Computes the `object-fit: contain` drawn size of an image with the given aspect
 * ratio (w/h) inside a box, then returns its longest edge in px. This is the
 * maxEdge the shadow fractions scale against — matching `computeContainFit` in
 * batchEditService for export parity.
 */
export function containMaxEdge(boxW: number, boxH: number, aspect: number): number {
  if (!boxW || !boxH || !aspect) return 0
  const dw = boxW / boxH > aspect ? boxH * aspect : boxW
  const dh = boxW / boxH > aspect ? boxH : boxW / aspect
  return Math.max(dw, dh)
}

/**
 * Builds a CSS `drop-shadow(...)` filter value from a ShadowConfig and the drawn
 * cutout's longest edge in px. `floor` matches the editor's integer offsets/blur
 * for export; the preview leaves it off for smoother updates.
 */
export function shadowToCssFilter(shadow: ShadowConfig, maxEdgePx: number, floor = false): string {
  const round = floor ? Math.floor : (n: number) => n
  const offsetX = round(Math.cos(shadow.angle) * shadow.distance * maxEdgePx)
  const offsetY = round(Math.sin(shadow.angle) * shadow.distance * maxEdgePx)
  const blurPx = round(shadow.blur * maxEdgePx)
  return `drop-shadow(${offsetX}px ${offsetY}px ${blurPx}px ${hexToRgba(shadow.color, shadow.opacity)})`
}
